import { getLandingPageByType, LANDING_PAGE_SOCIAL_PLATFORMS, LANDING_PAGE_TYPES } from './custom-pages/pageConfig';

const joinAddressParts = (parts = [], separator = ', ') => parts
  .map((part) => (typeof part === 'string' ? part.trim() : ''))
  .filter(Boolean)
  .join(separator);

const formatStructuredAddress = (address) => {
  if (!address || typeof address !== 'object') {
    return '';
  }

  const lineOne = joinAddressParts([address.street1 || address.street, address.street2], ', ');
  const lineTwo = joinAddressParts([address.city, address.state, address.zipCode], ', ')
    .replace(/, (?=\d)/, ' ');

  return [lineOne, lineTwo].filter(Boolean).join(', ');
};

export const getBusinessAddress = (companyData) => {
  const contactPage = getLandingPageByType(companyData?.landingPageSettings, LANDING_PAGE_TYPES.CONTACT, companyData);
  const contactAddress = contactPage?.contactFields?.address;
  if (typeof contactAddress === 'string' && contactAddress.trim()) {
    return contactAddress.trim();
  }

  const structuredBusinessAddress = formatStructuredAddress(companyData?.businessInfo?.address);
  if (structuredBusinessAddress) {
    return structuredBusinessAddress;
  }

  if (typeof companyData?.address === 'string' && companyData.address.trim()) {
    return companyData.address.trim();
  }

  return formatStructuredAddress(companyData?.address);
};

export const getAppointmentLocationAddress = (appointment, companyData) => {
  if (appointment?.locationAddress) {
    const structured = formatStructuredAddress(appointment.locationAddress);
    if (structured) return structured;
  }

  if (typeof appointment?.locationAddress === 'string' && appointment.locationAddress.trim()) {
    return appointment.locationAddress.trim();
  }

  return getBusinessAddress(companyData);
};

export const getBusinessEmail = (companyData) => {
  const contactPage = getLandingPageByType(companyData?.landingPageSettings, LANDING_PAGE_TYPES.CONTACT, companyData);
  const contactEmail = contactPage?.contactFields?.email;
  if (typeof contactEmail === 'string' && contactEmail.trim()) {
    return contactEmail.trim();
  }

  if (typeof companyData?.businessInfo?.email === 'string' && companyData.businessInfo.email.trim()) {
    return companyData.businessInfo.email.trim();
  }

  return '';
};

export const getBusinessPhone = (companyData) => {
  const contactPage = getLandingPageByType(companyData?.landingPageSettings, LANDING_PAGE_TYPES.CONTACT, companyData);
  const contactPhone = contactPage?.contactFields?.phone;

  if (typeof contactPhone === 'string' && contactPhone.trim()) {
    return contactPhone.trim();
  }

  if (typeof companyData?.phone === 'string' && companyData.phone.trim()) {
    return companyData.phone.trim();
  }

  return '';
};

const digitsFromPhoneString = (phone) => (typeof phone === 'string' ? phone.replace(/\D/g, '') : '');

/**
 * Human-readable phone for UI (NANP and simple grouping for other digit strings).
 * Use {@link getPhoneTelHref} for `tel:` links.
 */
export const formatPhoneForDisplay = (phone) => {
  const trimmed = typeof phone === 'string' ? phone.trim() : '';
  if (!trimmed) return '';

  const digits = digitsFromPhoneString(trimmed);
  if (!digits) return trimmed;

  if (digits.length === 11 && digits.startsWith('1')) {
    const n = digits.slice(1);
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  if (trimmed.startsWith('+')) {
    return `+${digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim()}`;
  }
  if (digits.length > 10 || (digits.length === 11 && !digits.startsWith('1'))) {
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  }

  return trimmed;
};

/** RFC-friendly `tel:` href for anchors (E.164 when possible for NANP). */
export const getPhoneTelHref = (phone) => {
  const trimmed = typeof phone === 'string' ? phone.trim() : '';
  if (!trimmed) return '';

  const hadPlus = trimmed.startsWith('+');
  const digits = digitsFromPhoneString(trimmed);
  if (!digits) return `tel:${encodeURIComponent(trimmed)}`;

  if (digits.length === 11 && digits.startsWith('1')) {
    return `tel:+${digits}`;
  }
  if (digits.length === 10) {
    return `tel:+1${digits}`;
  }
  if (hadPlus || digits.length > 10) {
    return `tel:+${digits}`;
  }
  return `tel:${digits}`;
};

const normalizeSocialUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  const trimmedValue = value.trim();
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

const getInstagramDisplayName = (url) => {
  try {
    const parsedUrl = new URL(url);
    const [firstSegment] = parsedUrl.pathname.split('/').filter(Boolean);

    if (!firstSegment) {
      return 'Instagram';
    }

    return firstSegment;
  } catch (_) {
    return 'Instagram';
  }
};

export const getBusinessSocialLinks = (companyData) => {
  const rawSources = [
    companyData?.landingPageSettings?.socialLinks,
    companyData?.socialLinks,
    companyData?.socialMedia,
    companyData?.businessInfo?.socialLinks,
    companyData?.businessInfo?.socialMedia
  ].filter((source) => source && typeof source === 'object');

  return LANDING_PAGE_SOCIAL_PLATFORMS.map((platform) => {
    const directValue = companyData?.[platform.key] || companyData?.businessInfo?.[platform.key];
    const nestedValue = rawSources
      .map((source) => source?.[platform.key])
      .find((value) => typeof value === 'string' && value.trim());
    const url = normalizeSocialUrl(nestedValue || directValue);

    if (!url) {
      return null;
    }

    return {
      ...platform,
      url,
      displayLabel: platform.key === 'instagram'
        ? getInstagramDisplayName(url)
        : platform.label
    };
  }).filter(Boolean);
};
